<template>
  <div class="grid grid-cols-3 items-center gap-3">
    <div class="col-span-2 flex items-start gap-3">
      <div class="pointer-events-none flex min-w-[56px] max-w-[56px] flex-col gap-0.5">
        <InfuseConnectorIcon
          size="standard"
          :status="connectorStatusForConnector(connector)"
          :type="baseTypeForConnector(connector)"
        />
        <div
          class="text-center text-[0.6rem] leading-[0.875rem] disabled:cursor-default"
          :class="[
            EViolinTextColorClasses[connectorStatusForConnector(connector)],
            connector.statusOverride && connector.statusOverride !== StatusOverride.UNKNOWN ? 'line-through' : ''
          ]"
        >
          {{ t(`connector.status.${connectorStatusForConnector(connector).toUpperCase()}`) }}
        </div>
        <div
          v-if="connector.statusOverride && connector.statusOverride !== StatusOverride.UNKNOWN"
          class="text-center text-[0.6rem] leading-[0.875rem] text-warning-500 disabled:cursor-default"
        >
          {{ t(`connector.statusOverride.${connector.statusOverride.toUpperCase()}`) }}
        </div>
      </div>

      <div class="flex w-full flex-col gap-2 self-center pt-0.5">
        <div class="flex items-center gap-4 text-[0.6rem] font-semibold text-neutral-600">
          <div>
            <font-awesome-icon :fixed-width="true" :icon="['fas', 'plug']" />
            {{ chargingStation.isOcpp1x ? evse.ocppEvseId : connector.connectorId }}
          </div>
          <div>{{ connector.maxAmp }} A</div>
          <div>{{ connector.voltage }} V</div>
          <div>{{ connector.current }}</div>
          <div>
            <InfuseTooltip
              :content="t('action.updateRoaming.modalDescription')"
              :icon="['far', 'pen-to-square']"
              :title="t('action.updateRoaming.modalTitle')"
              class="col-span-2 mb-2 font-normal normal-case"
              offset-distance="6"
            >
              <font-awesome-icon
                v-permission="{ id: commandPermissions.UPDATE_CONNECTOR_STATICS }"
                :icon="['far', 'pen-to-square']"
                size="1x"
                class="cursor-pointer text-neutral-400 opacity-0 hover:text-neutral-500 group-hover:opacity-100"
                @click="editConnectorRoamingSettingsModalVisible = true"
              />
            </InfuseTooltip>
          </div>
        </div>

        <div class="flex items-center">
          <div class="text-[0.6rem] text-neutral-700">
            {{ t(`connector.type.${connectorType(connector.type)}`) }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="chargingStation.accepted" class="col-span-1 grid grid-cols-1 justify-items-end gap-2">
      <div class="flex items-center gap-2">
        <p
          v-if="connector.availability === Availability.OPERATIVE"
          class="text-[0.625rem] font-medium text-neutral-700"
        >
          {{ t('label.operative') }}
        </p>
        <p v-else class="text-[0.625rem] font-medium text-neutral-700">{{ t('label.inoperative') }}</p>

        <InfuseToggle
          v-permission="{ id: commandPermissions.REQUEST_CHARGING_STATION_CHANGE_AVAILABILITY, hide: false }"
          :id="`connector-${connector.id}`"
          :value="connector.availability === Availability.OPERATIVE"
          :loading="isChangingAvailability"
          @on="setOperative(evse.ocppEvseId, connector.connectorId)"
          @off="setInoperative(evse.ocppEvseId, connector.connectorId)"
        />
      </div>

      <div class="flex gap-1">
        <InfuseSquareIconButton
          v-permission="{ id: commandPermissions.TRIGGER_MESSAGE, hide: false }"
          :icon="['fas', 'reply-all']"
          :tooltip-text="t('action.triggerMessage.name')"
          color="secondary"
          size="sm"
          @click="triggerMessageModalVisible = true"
        />

        <InfuseSquareIconButton
          v-permission="{ id: commandPermissions.START_TRANSACTION, hide: false }"
          :icon="['fas', 'play']"
          :tooltip-text="t('action.startTransaction.name')"
          color="secondary"
          size="sm"
          @click="startTransactionModalVisible = true"
        />

        <InfuseSquareIconButton
          v-permission="{ id: commandPermissions.UNLOCK_CONNECTOR, hide: false }"
          :icon="['fas', 'unlock']"
          :tooltip-text="t('action.unlockConnector.name')"
          color="primary"
          size="sm"
          @click="unlockConnectorModalVisible = true"
        />

        <InfuseSquareIconButton
          v-if="showOutOfOrderButton"
          v-permission="{ id: commandPermissions.OVERRIDE_OCPI_EVSE_STATUS, hide: false }"
          :icon="['fas', 'user-helmet-safety']"
          :tooltip-text="
            t(`action.setOutOfOrder.${connector.statusOverride === StatusOverride.OUTOFORDER ? 'off' : 'on'}.name`)
          "
          color="primary"
          size="sm"
          @click="showOutOfOrderModal"
        />
      </div>
    </div>
  </div>

  <div
    v-if="connector.status == ComponentStatus.FAULTED"
    class="border-error-50 border flex flex-row p-2 mt-2 rounded-md gap-1.5 items-center ml-1"
  >
    <font-awesome-icon :icon="['fas', 'circle-exclamation']" :fixed-width="true" class="text-error-400" />
    <span class="font-semibold text-[0.6rem]">
      {{ `${connector.errorCode}${connector.vendorErrorCode ? ' (' + connector.vendorErrorCode + ')' : ''}:` }}
    </span>
    <span class="text-[0.6rem]">{{ connector.errorInfo || '-' }}</span>
  </div>

  <EditConnectorRoamingDetailsModal
    v-if="editConnectorRoamingSettingsModalVisible"
    :connector="connector"
    :evse="evse"
    @close="editConnectorRoamingSettingsModalVisible = false"
    @save="updateConnectorRoamingSettings"
  />

  <OutOfOrderModal
    v-if="outOfOrderModalVisible"
    :connector-id="evse.ocppEvseId"
    :override-status="currentStatusOverride"
    @close="outOfOrderModalVisible = false"
    @toggle-out-of-order="setOutOfOrder"
  />

  <SendTriggerMessageModal
    v-if="triggerMessageModalVisible"
    :evse-id="evse.ocppEvseId"
    :connector-id="connector.connectorId"
    :ocppVersion="chargingStation.protocol"
    @close="triggerMessageModalVisible = false"
    @send="sendTriggerMessage"
  />

  <StartRemoteTransactionModal
    v-if="startTransactionModalVisible && evse.id"
    :evse-id="evse.ocppEvseId"
    :charging-station="chargingStation"
    @close="startTransactionModalVisible = false"
    @start="startRemoteTransaction"
  />

  <InfuseConfirmModal
    v-if="unlockConnectorModalVisible"
    :action-title="t('action.unlockConnector.modalTitle')"
    :action-description="t('action.unlockConnector.modalDescription')"
    :verification-required="false"
    @close="unlockConnectorModalVisible = false"
    @confirm="unlock(evse.ocppEvseId, connector.connectorId)"
  />
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  commandPermissions,
  MessageTrigger,
  OcpiStatus,
  overrideOcpiEvseStatus,
  startTransaction,
  TokenType,
  triggerMessage,
  unlockConnector,
  updateConnectorStatics
} from '@infuse/api/cpms/operator';
import { useChangeAvailability } from '@infuse/cpms/charging-station/mutations';
import {
  Availability,
  baseTypeForConnector,
  ChargingStation,
  ComponentStatus,
  Connector,
  connectorStatusForConnector,
  Evse,
  StatusOverride
} from '@infuse/cpms/charging-station/store';
import { vPermission } from '@infuse/iam/permission/directives';
import {
  InfuseConfirmModal,
  InfuseConnectorIcon,
  InfuseSquareIconButton,
  InfuseToggle,
  InfuseTooltip
} from '@infuse/ui/components';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import axios from 'axios';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import EditConnectorRoamingDetailsModal from './EditConnectorRoamingDetailsModal.vue';
import OutOfOrderModal from './OutOfOrderModal.vue';
import SendTriggerMessageModal from './SendTriggerMessageModal.vue';
import StartRemoteTransactionModal from './StartRemoteTransactionModal.vue';
import { ConnectorRoamingSettings } from './types/RoamingSettings';
import { UiVariantNames } from '@infuse/ui/domain';
import { EViolinTextColorClasses } from '@infuse/ui/utils';

const notify = useNotify();
const { t } = useI18n();

const { VITE_UI_VARIANT_CONNECTOR_DETAILS_OUT_OF_ORDER } = import.meta.env || '';
const { mutateAsync: changeAvailability, isPending: isChangingAvailability } = useChangeAvailability();

interface Props {
  chargingStation: ChargingStation;
  connector: Connector;
  evse: Evse;
}

const props = defineProps<Props>();

interface Emits {
  (e: 'reload-model'): void;
}

const emit = defineEmits<Emits>();

const editConnectorRoamingSettingsModalVisible = ref(false);
const outOfOrderModalVisible = ref(false);
const startTransactionModalVisible = ref(false);
const triggerMessageModalVisible = ref(false);
const unlockConnectorModalVisible = ref(false);

const currentStatusOverride = ref<StatusOverride | undefined>();

const connectorType = (type: string) => {
  if (type === 'Unspecified') {
    return t('label.connectorType.unspecified');
  }
  return type;
};

const showOutOfOrderButton = computed(() => {
  return VITE_UI_VARIANT_CONNECTOR_DETAILS_OUT_OF_ORDER !== UiVariantNames.CONNECTOR_DETAILS_OUT_OF_ORDER_DISABLED;
});

function showOutOfOrderModal() {
  currentStatusOverride.value = props.connector.statusOverride;
  outOfOrderModalVisible.value = true;
}

async function startRemoteTransaction(evseId: number, token: string, tokenType?: TokenType) {
  try {
    await startTransaction(
      { chargingStationId: props.chargingStation.id },
      { evseId: evseId, identifyingToken: { token, tokenType } }
    );
    notifySuccess('startTransaction', { connector: evseId });
    startTransactionModalVisible.value = false;
    emit('reload-model');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      notifyError('startTransaction', error.response?.status, { connector: evseId });
    } else {
      throw error;
    }
  }
}

const sendTriggerMessage = async (
  evseId: number | undefined,
  connectorId: number | undefined,
  messageType: MessageTrigger,
  customTrigger: string | undefined
) => {
  if (!props.connector) {
    return;
  }

  const forStation = !evseId;
  try {
    await triggerMessage(
      { chargingStationId: props.chargingStation.id },
      { connectorId, evseId, messageTrigger: messageType, customTrigger }
    );
    notifySuccess('triggerMessage', {
      message: messageType,
      component: t(`action.triggerMessage.${forStation ? 'chargingStation' : 'connector'}`),
      componentId: forStation ? props.chargingStation.id : `${evseId}-${connectorId}`
    });
    triggerMessageModalVisible.value = false;
    emit('reload-model');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      notifyError('triggerMessage', error.response?.status, {
        message: messageType,
        component: t(`action.triggerMessage.${forStation ? 'chargingStation' : 'connector'}`),
        componentId: forStation ? props.chargingStation.id : `${evseId}-${connectorId}`
      });
    } else {
      throw error;
    }
  }
};

const setInoperative = async (evseId: number, connectorId?: number) => {
  if (!props.connector) {
    return;
  }

  await changeAvailability({
    chargingStationId: props.chargingStation.id,
    evseId,
    connectorId,
    availability: Availability.INOPERATIVE
  });
  emit('reload-model');
};

const setOperative = async (evseId: number, connectorId?: number) => {
  if (!props.connector) {
    return;
  }

  await changeAvailability({
    chargingStationId: props.chargingStation.id,
    evseId,
    connectorId,
    availability: Availability.OPERATIVE
  });
  emit('reload-model');
};

const setOutOfOrder = async () => {
  if (!props.connector) {
    return;
  }

  const notifyKey = `setOutOfOrder.${currentStatusOverride.value !== StatusOverride.OUTOFORDER ? 'on' : 'off'}`;

  try {
    await overrideOcpiEvseStatus(
      { chargingStationId: props.chargingStation.id },
      {
        evseId: props.evse.ocppEvseId,
        status: currentStatusOverride.value !== StatusOverride.OUTOFORDER ? OcpiStatus.OUTOFORDER : OcpiStatus.UNKNOWN
      }
    );
    outOfOrderModalVisible.value = false;
    notifySuccess(notifyKey, { connector: props.evse.ocppEvseId });
    emit('reload-model');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      notifyError(notifyKey, error.response?.status, { connector: props.evse.ocppEvseId });
    } else {
      throw error;
    }
  }
};

const unlock = async (evseId?: number, connectorId?: number) => {
  if (!props.connector || !evseId || !connectorId) {
    return;
  }

  try {
    await unlockConnector({ chargingStationId: props.chargingStation.id }, { evseId, connectorId });
    notifySuccess('unlockConnector', { connector: evseId });
    unlockConnectorModalVisible.value = false;
    emit('reload-model');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      notifyError('unlockConnector', error.response?.status, { connector: evseId });
    } else {
      throw error;
    }
  }
};

const updateConnectorRoamingSettings = async (updatedConnectorRoamingSettings: ConnectorRoamingSettings) => {
  if (!updatedConnectorRoamingSettings) {
    return;
  }

  try {
    await updateConnectorStatics(
      { chargingStationId: props.chargingStation.id },
      {
        connectors: [
          {
            evseId: {
              numberedId: updatedConnectorRoamingSettings.evseId
            },
            connectorType: updatedConnectorRoamingSettings.connectorType,
            current: updatedConnectorRoamingSettings.current,
            maxAmperage: updatedConnectorRoamingSettings.maxAmperage,
            maxElectricPower: updatedConnectorRoamingSettings.maxElectricPower,
            phase: updatedConnectorRoamingSettings.phase,
            physicalReference: updatedConnectorRoamingSettings.physicalReference,
            voltage: updatedConnectorRoamingSettings.voltage
          }
        ]
      }
    );

    notifySuccess('updateRoaming');

    editConnectorRoamingSettingsModalVisible.value = false;
    emit('reload-model');
  } catch (error) {
    notifyError('updateRoaming', axios.isAxiosError(error) ? error.response?.status : 500, {
      reason: axios.isAxiosError(error) ? ` '${error.response?.data?.message}'` : ''
    });
  }
};

const notifySuccess = (key: string, msgProps = {}, timeout = 5000) => {
  notify({
    type: UiNotificationType.SUCCESS,
    group: 'notify-top',
    title: t(`action.${key}.response.success.title`),
    message: t(`action.${key}.response.success.message`, { ...msgProps, csId: props.chargingStation.id }),
    timeout
  });
};

const notifyError = (key: string, code = 500, msgProps = {}, timeout = 5000) => {
  const tCode = [400, 404, 500].includes(code) ? code : 500;

  notify({
    type: UiNotificationType.ERROR,
    group: 'notify-top',
    title: t(`action.${key}.response.error.${tCode}.title`),
    message: t(`action.${key}.response.error.${tCode}.message`, { ...msgProps, csId: props.chargingStation.id }),
    timeout
  });
};
</script>

<i18n>
{
  "en": {
    "connector": {
      "status": {
        "INACTIVE": "Inactive",
        "ERROR": "Error",
        "FULLYCHARGED": "Fully charged",
        "CHARGING": "Charging",
        "ACTIVE": "Active",
        "AVAILABLE": "Available"
      },
      "statusOverride": {
        "AVAILABLE": "Available",
        "BLOCKED": "Blocked",
        "CHARGING": "Charging",
        "INOPERATIVE": "Inoperative",
        "OUTOFORDER": "Out of order",
        "PLANNED": "Planned",
        "REMOVED": "Removed",
        "RESERVED": "Reserved"
      },
      "type": {
        "UNSPECIFIED": "Not specified",
        "C_TYPE_1": "Cabled, IEC62196-2 Type 1 (J1772)",
        "C_TYPE_2": "Cabled, IEC62196-2 Type 2 (Mennekes)",
        "C_G105": "Cabled, CHAdeMO",
        "C_CCS_1": "Cabled, Combined Charging System 1 (Combo 1)",
        "C_CCS_2": "Cabled, Combined Charging System 2 (Combo 2)",
        "CEE_7_7": "Socket, CEE 7/7 (Schuko), 16A",
        "S_309_1P_16A": "Socket, IEC60309, single phase, 16A",
        "S_309_3P_16A": "Socket, IEC60309, three phase, 16A",
        "S_309_3P_32A": "Socket, IEC60309, three phase, 32A",
        "S_TYPE_2": "Socket IEC62196-2 Type 2 (Mennekes)",
        "S_TYPE_3": "Socket IEC62196-2 Type 3 (Scame)",
        "S_BS1361": "Socket, UK domestic"
      }
    },
    "label": {
      "connector": "Connector {0}",
      "operative": "Operative",
      "inoperative": "Inoperative",
      "connectorType": {
        "unspecified": "Unspecified"
      }
    },
    "action": {
      "setOutOfOrder": {
        "off": {
          "name": "Remove out of order",
          "response": {
            "success": {
              "title": "Request to remove out of order",
              "message": "The request to unset the connector as out of order has been sent."
            },
            "error": {
              "400": {
                "title": "Request to remove out of order failed",
                "message": "Something went wrong while requesting to unset the connector as out of order."
              },
              "404": {
                "title": "Charging station not found",
                "message": "Charging station '{csId}' could not be found."
              },
              "500": {
                "title": "Unexpected error",
                "message": "An unexpected error occurred while requesting to unset the connector as out of order."
              }
            }
          }
        },
        "on": {
          "name": "Set out of order",
          "response": {
            "success": {
              "title": "Request to set out of order sent",
              "message": "The request to set the connector as out of order has been sent."
            },
            "error": {
              "400": {
                "title": "Request to remove out of order failed",
                "message": "Something went wrong while requesting to set the connector as out of order."
              },
              "404": {
                "title": "Charging station not found",
                "message": "Charging station '{csId}' could not be found."
              },
              "500": {
                "title": "Unexpected error",
                "message": "An unexpected error occurred while requesting to set the connector as out of order."
              }
            }
          }
        }
      },
      "startTransaction": {
        "name": "Start transaction",
        "response": {
          "success": {
            "title": "Request to start transaction sent",
            "message": "The request to start a transaction on connector {connector} has been sent."
          },
          "error": {
            "400": {
              "title": "Request to start transaction failed",
              "message": "Something went wrong while requesting to start transaction on connector '{connector}'."
            },
            "404": {
              "title": "Charging station not found",
              "message": "Charging station '{csId}' could not be found."
            },
            "500": {
              "title": "Unexpected error",
              "message": "An unexpected error occurred while requesting to start transaction on connector '{connector}'."
            }
          }
        }
      },
      "triggerMessage": {
        "name": "Request message",
        "chargingStation": "charging station",
        "connector": "connector",
        "response": {
          "success": {
            "title": "Request for message sent",
            "message": "The request for {component} '{componentId}' to send a {message} has been sent."
          },
          "error": {
            "400": {
              "title": "Requesting message failed",
              "message": "Something went wrong while requesting {component} '{componentId}' to send a {message}."
            },
            "404": {
              "title": "Charging station not found",
              "message": "Charging station '{csId}' could not be found."
            },
            "500": {
              "title": "Unexpected error",
              "message": "An unexpected error occurred while requesting {component} '{componentId}' to send a {message}."
            }
          }
        }
      },
      "unlockConnector": {
        "name": "Unlock connector",
        "modalTitle": "Unlock",
        "modalDescription": "If the charging station has an ongoing transaction on this connector, it will be stopped.",
        "response": {
          "success": {
            "title": "Request to unlock connector sent",
            "message": "The request to unlock connector '{connector}' has been sent. Any ongoing transaction on this connector may be stopped by the charging station."
          },
          "error": {
            "400": {
              "title": "Requesting unlock failed",
              "message": "Something went wrong while requesting to unlock '{connector}' on '{csId}'."
            },
            "404": {
              "title": "Charging station not found",
              "message": "Charging station '{csId}' could not be found."
            },
            "500": {
              "title": "Unexpected error",
              "message": "An unexpected error occurred while requesting to unlock '{connector}' on '{csId}'."
            }
          }
        }
      },
      "updateRoaming": {
        "modalTitle": "Edit roaming settings",
        "modalDescription": "The configured roaming settings for this connector. These settings do not influence the ability to manage the charging station, and are only used to improve the charging experience of EV drivers.",
        "response": {
          "success": {
            "title": "Update successful",
            "message": "Connector's roaming settings have been updated."
          },
          "error": {
            "400": {
              "title": "Update failed",
              "message": "Failed to update the connector's roaming settings. {reason}"
            },
            "404": {
              "title": "Charging station not found",
              "message": "Charging station '{csId}' could not be found."
            },
            "500": {
              "title": "Unexpected error",
              "message": "An unexpected error occurred while requesting to update the connector's roaming settings."
            }
          }
        }
      }
    }
  },
  "nl": {
    "connector": {
      "status": {
        "INACTIVE": "Inactief",
        "ERROR": "Error",
        "FULLYCHARGED": "Opgeladen",
        "CHARGING": "Opladen",
        "ACTIVE": "Actief",
        "AVAILABLE": "Beschikbaar"
      },
      "statusOverride": {
        "AVAILABLE": "Beschikbaar",
        "BLOCKED": "Geblokkeerd",
        "CHARGING": "Opladen",
        "INOPERATIVE": "Buiten werking",
        "OUTOFORDER": "Buiten dienst",
        "PLANNED": "Gepland",
        "REMOVED": "Verwijderd",
        "RESERVED": "Gereserveerd"
      },
      "type": {
        "UNSPECIFIED": "Niet gespecificeerd",
        "C_TYPE_1": "Vaste kabel, IEC62196-2 Type 1 (J1772)",
        "C_TYPE_2": "Vaste kabel, IEC62196-2 Type 2 (Mennekes)",
        "C_G105": "Vaste kabel, CHAdeMO",
        "C_CCS_1": "Vaste kabel, Combined Charging System 1 (Combo 1)",
        "C_CCS_2": "Vaste kabel, Combined Charging System 2 (Combo 2)",
        "CEE_7_7": "Socket, CEE 7/7 (Schuko), 16A",
        "S_309_1P_16A": "Socket, IEC60309, enkele fase, 16A",
        "S_309_3P_16A": "Socket, IEC60309, drie fasen, 16A",
        "S_309_3P_32A": "Socket, IEC60309, drie fasen, 32A",
        "S_TYPE_2": "Socket, IEC62196-2 Type 2 (Mennekes)",
        "S_TYPE_3": "Socket, IEC62196-2 Type 3 (Scame)",
        "S_BS1361": "Socket, UK domestic"
      }
    },
    "label": {
      "connector": "Connector {0}",
      "operative": "Operatief",
      "inoperative": "Niet operatief",
      "connectorType": {
        "unspecified": "Niet opgegeven"
      }
    },
    "action": {
      "setOutOfOrder": {
        "off": {
          "name": "Terug in dienst",
          "response": {
            "success": {
              "title": "Verzoek tot terug in dienst zetten",
              "message": "Het verzoek om de aansluiting terug in dienst te zetten is verstuurd."
            },
            "error": {
              "400": {
                "title": "Verzoek tot terug in dienst zetten mislukt",
                "message": "Er ging iets fout tijdens het versturen van het verzoek om de aansluiting terug in dienst te zetten."
              },
              "404": {
                "title": "Laadpaal niet gevonden",
                "message": "laadpaal '{csId}' kon niet worden gevonden."
              },
              "500": {
                "title": "Onverwachte fout",
                "message": "Er ging onverwacht iets fout tijdens het versturen van het verzoek om de aansluiting terug in dienst te zetten."
              }
            }
          }
        },
        "on": {
          "name": "Buiten dienst",
          "response": {
            "success": {
              "title": "Verzoek tot buiten dienst zetten",
              "message": "Het verzoek om de aansluiting buiten dienst te zetten is verstuurd."
            },
            "error": {
              "400": {
                "title": "Verzoek tot buiten dienst zetten mislukt",
                "message": "Er ging iets fout tijdens het versturen van het verzoek om de aansluiting buiten dienst te zetten."
              },
              "404": {
                "title": "Laadpaal niet gevonden",
                "message": "Laadpaal '{csId}' kon niet worden gevonden."
              },
              "500": {
                "title": "Onverwachte fout",
                "message": "Er ging onverwacht iets fout tijdens het versturen van het verzoek om de aansluiting buiten dienst te zetten."
              }
            }
          }
        }
      },
      "startTransaction": {
        "name": "Transactie starten",
        "response": {
          "success": {
            "title": "Verzoek voor starten transactie verstuurd",
            "message": "Het verzoek om een transactie te starten op aansluiting '{connector}' is verstuurd."
          },
          "error": {
            "400": {
              "title": "Starten van transactie mislukt",
              "message": "Er ging iets fout tijdens het versturen van het verzoek om een transactie te starten op aansluiting '{connector}'."
            },
            "404": {
              "title": "Laadpaal niet gevonden",
              "message": "Laadpaal '{csId}' kon niet worden gevonden."
            },
            "500": {
              "title": "Onverwachte fout",
              "message": "Er ging onverwacht iets fout tijdens het versturen van het verzoek om een transactie te starten op aansluiting '{connector}'."
            }
          }
        }
      },
      "triggerMessage": {
        "name": "Bericht aanvragen",
        "chargingStation": "laadpaal",
        "connector": "aansluiting",
        "response": {
          "success": {
            "title": "Verzoek voor bericht verstuurd",
            "message": "Het verzoek aan '{componentId}' om een {message} te verzenden is verstuurd."
          },
          "error": {
            "400": {
              "title": "Requesting message failed",
              "message": "Er ging iets fout tijdens het versturen van het verzoek aan {component} '{componentId}' om een {message} te versturen."
            },
            "404": {
              "title": "Laadpaal niet gevonden",
              "message": "Laadpaal '{csId}' kon niet worden gevonden."
            },
            "500": {
              "title": "Onverwachte fout",
              "message": "Er ging onverwacht iets fout tijdens het versturen van het verzoek aan {component} '{componentId}' om een {message} te versturen."
            }
          }
        }
      },
      "unlockConnector": {
        "name": "Aansluiting ontgrendelen",
        "modalTitle": "Ontgrendelen",
        "modalDescription": "Als op deze aansluiting momenteel een transactie actief is, dan zal de laadpaal deze stoppen.",
        "response": {
          "success": {
            "title": "Verzoek om aansluiting te ontgrendelen verstuurd",
            "message": "Het verzoek om aansluiting '{connector}' te ontgrendelen is verstuurd. Indien er een transactie actief is zal deze door de laadpaal worden gestopt."
          },
          "error": {
            "400": {
              "title": "Ontgrendelen van aansluiting mislukt",
              "message": "Er ging iets fout tijdens het versturen van het verzoek tot ontgrendelen van aansluiting {connectorId} op laadpaal '{csId}'."
            },
            "404": {
              "title": "Laadpaal niet gevonden",
              "message": "Laadpaal '{csId}' kon niet worden gevonden."
            },
            "500": {
              "title": "Onverwachte fout",
              "message": "Er ging onverwacht iets fout tijdens het versturen van het verzoek tot ontgrendelen van aansluiting {connectorId} op laadpaal '{csId}'."
            }
          }
        }
      },
      "updateRoaming": {
        "modalTitle": "Roaming-instellingen aanpassen",
        "modalDescription": "De ingestelde roaming-instellingen voor deze aansluiting. Deze instellingen hebben geen invloed op het kunnen beheren van de laadpaal, en worden enkel gebruikt om de laad-ervaring van EV-bereiders te verbeteren.",
        "response": {
          "success": {
            "title": "Aanpassen gelukt",
            "message": "De roaming-instellingen van de aansluiting zijn aangepast."
          },
          "error": {
            "400": {
              "title": "Aanpassen mislukt",
              "message": "De roaming-instellingen van de aansluiting konden niet worden aangepast. {reason}"
            },
            "404": {
              "title": "Laadpaal niet gevonden",
              "message": "Laadpaal '{csId}' kon niet worden gevonden."
            },
            "500": {
              "title": "Onverwachte fout",
              "message": "Er ging onverwacht iets fout tijdens het aanpassen van de roaming-instellingen van de aansluiting."
            }
          }
        }
      }
    }
  }
}
</i18n>
